import { CommunityHeaderCard } from "@components/Community/CommunityHeaderCard";
import { Donations } from "@components/Community/Donations";
import { Nonprofits } from "@components/Community/Nonprofits";
import {
  TabNavigation,
  CommunityPageTabs,
} from "@components/Community/TabNavigation";
import { LearnMore } from "@components/Funds/LearnMore";
import { DefaultPageLayout } from "@components/layout/DefaultPageLayout";
import { ImageHeaderPageLayout } from "@components/layout/ImageHeaderPageLayout";
import { DefaultPageSection } from "@components/layout/PageSection";
import { css } from "@emotion/react";
import React, { Fragment, useState, useCallback, useEffect } from "react";

import { SafeInt } from "@every.org/common/src/codecs/number";
import { spacing } from "@every.org/common/src/display/spacing";
import { NonprofitType } from "@every.org/common/src/entity/types";
import {
  clientRouteMetas,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";
import { constructCloudinaryUrl } from "@every.org/common/src/helpers/cloudinary";
import { currencyValueToMinimumDenominationAmount } from "@every.org/common/src/helpers/currency";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";
import {
  getCommunityImpactRouteSpec,
  GetCommunityImpactResponse,
} from "@every.org/common/src/routes/community";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { NotFoundPage } from "src/pages/NotFound";
import {
  cssForMediaSize,
  MediaSize,
  useMatchesScreenSize,
} from "src/theme/mediaQueries";
import { verticalStackCss } from "src/theme/spacing";
import { ClientTrackingRoute, trackPageChange } from "src/utility/analytics";
import { queryApi } from "src/utility/apiClient";
import { nonprofitShareImageCloudinaryParams } from "src/utility/cloudinary/nonprofit";
import { logger } from "src/utility/logger";
import { OPENGRAPH_DIMENSIONS } from "src/utility/opengraph";
import { NONPROFIT_PAGE_HEADER_OVERLAP_HEIGHT } from "src/utility/pageMetadata";

type CommunityPageProps = { community: ContextNonprofit };

function selectedTabFromPath(
  match?: string | null,
  showRecommendationsTab?: boolean
) {
  if (match) {
    switch (match) {
      case clientRouteMetas[ClientRouteName.NONPROFIT_OR_CAUSE].path:
        return showRecommendationsTab
          ? CommunityPageTabs.RECOMMENDATIONS
          : CommunityPageTabs.COMMUNITY_FEED;
      case clientRouteMetas[ClientRouteName.COMMUNITY_RECOMMENDATIONS].path:
        return CommunityPageTabs.RECOMMENDATIONS;
      case clientRouteMetas[ClientRouteName.COMMUNITY_FEED].path:
        return CommunityPageTabs.COMMUNITY_FEED;
      case clientRouteMetas[ClientRouteName.FUND_LEARN_MORE].path:
        return CommunityPageTabs.LEARN_MORE;
    }
  }
  return undefined;
}

const CommunityPage: React.FCC<CommunityPageProps> = ({ community }) => {
  const { getPathnameWithParams } = useEdoRouter();
  const { path } = getPathnameWithParams();
  const showRecommendationsTab = !!community.endorsedNonprofitIds?.length;
  const [donationCount, setDonationCount] = useState(0);
  const [goalProgress, setGoalProgress] = useState<SafeInt | undefined>();

  useEffect(() => {
    trackPageChange(ClientTrackingRoute.COMMUNITY, community.name);
  }, [community.name]);

  const selectedTab = selectedTabFromPath(path, showRecommendationsTab);

  const isLargeScreen =
    // eslint-disable-next-line no-restricted-syntax
    useMatchesScreenSize({ min: MediaSize.XX_LARGE }) ?? false;

  const fetchCommunityMetadata = useCallback(() => {
    if (community.type === NonprofitType.COMMUNITY) {
      return queryApi(getCommunityImpactRouteSpec, {
        routeTokens: { id: community.id },
        body: {},
        queryParams: {},
      });
    }
    return Promise.resolve(undefined);
  }, [community.id, community.type]);
  const handleMetadata = useCallback(
    (response: GetCommunityImpactResponse | undefined) => {
      if (response) {
        setDonationCount(response.donationCount);
        setGoalProgress(
          currencyValueToMinimumDenominationAmount({
            value: response.amountRaised,
          }) as SafeInt
        );
      }
    },
    []
  );

  const handleError = useCallback((error: Error) => {
    logger.error({
      error,
      message: "An error occurred while fetching community metadata",
    });
  }, []);
  useAsyncEffect({
    asyncOperation: fetchCommunityMetadata,
    handleResponse: handleMetadata,
    handleError,
  });

  if (community.type !== NonprofitType.COMMUNITY) {
    logger.warn({
      message: `Attempted to render community page for a ${community.type} nonprofit.`,
      data: { nonprofit: community },
    });
    return <NotFoundPage />;
  }

  const {
    name,
    description,
    descriptionLong,
    coverImageCloudinaryId,
    logoCloudinaryId,
  } = community;

  const opengraphImageCloudinaryParams = nonprofitShareImageCloudinaryParams({
    logoCloudinaryId,
    coverImageCloudinaryId,
    imageHeight: OPENGRAPH_DIMENSIONS.height,
  });
  const metas = removeUndefinedOrNullValues({
    "og:description": description,
    "og:image":
      opengraphImageCloudinaryParams &&
      constructCloudinaryUrl({
        ...opengraphImageCloudinaryParams,
        dimensions: OPENGRAPH_DIMENSIONS,
      }),
  });

  const pageContents = (
    <React.Fragment>
      <DefaultPageSection>
        <div
          css={[
            verticalStackCss.xl,
            cssForMediaSize({
              max: MediaSize.SMALL,
              css: verticalStackCss.m,
            }),
          ]}
        >
          <CommunityHeaderCard
            community={community}
            goalProgress={goalProgress}
          />
          <Fragment>
            <TabNavigation
              community={community}
              selectedTab={selectedTab}
              donationCount={donationCount}
              css={[
                { marginBottom: spacing.xxl },
                cssForMediaSize({
                  max: MediaSize.SMALL,
                  css: { marginBottom: spacing.l },
                }),
              ]}
            />
            {selectedTab === CommunityPageTabs.RECOMMENDATIONS &&
              showRecommendationsTab && <Nonprofits community={community} />}
            {selectedTab === CommunityPageTabs.COMMUNITY_FEED && (
              <Donations community={community} />
            )}
            {selectedTab === CommunityPageTabs.LEARN_MORE && descriptionLong && (
              <LearnMore
                css={css`
                  margin-top: ${spacing.xl};
                `}
                content={descriptionLong}
              />
            )}
          </Fragment>
        </div>
      </DefaultPageSection>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {isLargeScreen || !community.coverImageCloudinaryId ? (
        <DefaultPageLayout
          pageTitle={name}
          metas={metas}
          philanthropyforEveryoneSuffix
        >
          {pageContents}
        </DefaultPageLayout>
      ) : (
        <ImageHeaderPageLayout
          pageTitle={name}
          metas={metas}
          headerOverlapHeight={NONPROFIT_PAGE_HEADER_OVERLAP_HEIGHT}
          imgCloudinaryId={community.coverImageCloudinaryId}
          philanthropyforEveryoneSuffix
        >
          {pageContents}
        </ImageHeaderPageLayout>
      )}
    </React.Fragment>
  );
};

export default CommunityPage;
